import slugify from "slugify";
import { EditorJs } from "src";
import {
	BlocksDynamicZone,
	EditorBlock,
	ReferenceBlock,
} from "src/types/blocks";
import { TextReturnTypes } from "src/types/editorjs";

import EditorJSParser from "./editorjs";

type TocOptions = {
	slug?: string;
	parent?: {
		title: string;
		link_title: string;
		slug: string;
	};
};
type HeadingsOptions = {
	includeHeadings?: number[];
};

type Blocks = EditorBlock | ReferenceBlock;
const findContentBlocks = (blocks: BlocksDynamicZone[]) => {
	return blocks.filter(
		(block) =>
			block.__component === "blocks.editor" ||
			block.__component === "blocks.lookup",
	) as Blocks[];
};
const findEditorBlocks = (blocks: BlocksDynamicZone[]) => {
	return blocks.filter(
		(block) => block.__component === "blocks.editor",
	) as EditorBlock[];
};
export const getEditorBlocks = (blocks: BlocksDynamicZone[]) => {
	const editorBlocks = findContentBlocks(blocks);
	const flattenedBlocks: EditorBlock[] = [];
	editorBlocks.forEach((block) => {
		if (block.__component === "blocks.editor") {
			return flattenedBlocks.push(block);
		}
		const options =
			block.reference_type !== "link" && block.lookup_options
				? block.lookup_options
				: {};
		if (typeof block.editor === "string") {
			flattenedBlocks.push({
				id: block.id,
				__component: "blocks.editor",
				editor: block.editor,
			});
		}
		for (const option of Object.keys(options)) {
			//@ts-ignore
			const nestedBlocks = options[option]?.data?.attributes?.blocks ?? [];
			const nestedEditorBlocks = findEditorBlocks(nestedBlocks);
			if (nestedEditorBlocks.length > 0) {
				// console.log('nested blocks', nestedEditorBlocks);
				flattenedBlocks.push(...nestedEditorBlocks);
			}
		}
	});
	// console.log('f blocks', flattenedBlocks);
	return flattenedBlocks;
};

export const getHeadings = (
	blocks: BlocksDynamicZone[],
	options: HeadingsOptions = {},
) => {
	const editorBlocks = getEditorBlocks(blocks);

	const reducer = (
		accumulator: EditorJs.Header[],
		currentValue: EditorBlock,
		currentIndex: number,
	) => {
		const headers = EditorJSParser.getHeaderBlocks(currentValue, options);
		return accumulator.concat(headers);
	};

	return editorBlocks.reduce(reducer, []);
};
export const getHeadingsWithAnchors = (
	blocks: BlocksDynamicZone[],
	options: HeadingsOptions = {},
) => {
	const headings = getHeadings(blocks);
	return headings.map((heading) => {
		return {
			...heading,
			anchorId: slugify(heading.data.text).toLowerCase(),
		};
	});
};

export const getTextBlocks = (
	blocks: BlocksDynamicZone[],
): EditorJs.TextReturnTypes[] => {
	const editorBlocks = getEditorBlocks(blocks);

	const reducer = (
		accumulator: TextReturnTypes[],
		currentValue: EditorBlock,
		currentIndex: number,
	) => {
		const text = EditorJSParser.getTextBlocks(currentValue);
		// console.log(JSON.stringify(text, null, 2));
		return accumulator.concat(text);
	};

	return editorBlocks.reduce(reducer, []);
};

export const getTableOfContents = (
	blocks: BlocksDynamicZone[],
	options: TocOptions = {},
) => {
	// filter out lookup blocks with reference_type === link
	const headings = getHeadingsWithAnchors(blocks);

	let currentParentIndex = -1;

	let results = [];
	for (let i = 0; i < headings.length; i++) {
		let heading = headings[i];
		if (!heading) continue;
		let isParent = heading?.data.level === 2;
		if (isParent) {
			results.push({
				...heading,
				...options,
				children: [] as EditorJs.Header[],
			});
			currentParentIndex = currentParentIndex + 1;
		} else {
			results[currentParentIndex]?.children.push(heading);
		}
	}
	return results;
};

export type SearchableContent = {
	breadcrumbs: string[];
	href: string;
	content: string;
	weight?: number;
};
type SearchableContentOptions = {
	baseUrl?: string;
	weight?: number;
	baseBreadCrumb?: string;
};

const startingBreadCrumbs = (baseBreadCrumb?: string) => {
	return baseBreadCrumb ? [plainText(baseBreadCrumb)] : [];
};
export const getSearchableContent = (
	blocks: BlocksDynamicZone[],
	options: SearchableContentOptions = {},
): SearchableContent[] => {
	const { baseUrl, baseBreadCrumb, weight = 0 } = options;
	const textBlocks = getTextBlocks(blocks);

	let results: SearchableContent[] = [];
	// h2 text, h3 text...
	let breadcrumbs: string[] = startingBreadCrumbs(baseBreadCrumb);

	for (let i = 0; i < textBlocks.length; i++) {
		// Only look at blocks designated as having text
		let textBlock = textBlocks[i];
		let lastBlock = i > 0 ? textBlocks[i - 1] : undefined;
		if (!textBlock) continue;

		let isHeading =
			"text" in textBlock.data &&
			"level" in textBlock.data &&
			textBlock.data.level;
		let lastResult = results[results.length - 1];

		let content = "";

		if ("text" in textBlock.data) {
			content = textBlock.data.text ?? "";
		}
		if (
			"items" in textBlock.data &&
			textBlock.data.items &&
			"length" in textBlock.data.items
		) {
			content = textBlock.data.items
				.map((item) => {
					if (typeof item === "string") {
						return ` ${item}`;
					}
					if ("text" in item) {
						return ` ${item.text}`;
					}
					return "";
				})
				.toString();
		}

		let anchor =
			isHeading && "text" in textBlock.data
				? `#${slugify(textBlock.data.text ?? "").toLowerCase()}`
				: "";
		let href = baseUrl ? baseUrl + anchor : "";
		if (!lastResult) {
			// Create a new result
			results.push({
				breadcrumbs:
					isHeading && "text" in textBlock.data
						? [...breadcrumbs, plainText(textBlock.data.text ?? "")]
						: breadcrumbs,
				content: isHeading ? "" : plainText(content),
				href: href,
				weight,
			});
		} else {
			if (isHeading) {
				if (
					lastBlock &&
					lastBlock.type === "header" &&
					"level" in textBlock.data &&
					textBlock.data.level > lastBlock.data.level
				) {
					lastResult.breadcrumbs = [
						...lastResult.breadcrumbs,
						plainText(
							"text" in textBlock.data ? textBlock.data.text ?? "" : "",
						),
					];
					lastResult.href = href;
				} else {
					results.push({
						breadcrumbs:
							isHeading && "text" in textBlock.data
								? [...breadcrumbs, plainText(textBlock.data.text ?? "")]
								: breadcrumbs,
						content: isHeading ? "" : plainText(content),
						href: href,
						weight,
					});
				}
			} else {
				lastResult.content += ` ${plainText(content)}`;
			}
		}
	}

	//   // const anchorText = lastResult?.data.text ?? null;
	//   // const anchor = typeof anchorText === 'string' ? `#${slugify(anchorText).toLowerCase()}` : '';
	//   // const href = baseUrl ? `${baseUrl}${anchor}` : anchor;
	return results;
};

function plainText(text: string) {
	return text
		.replace(/(<([^>]+)>)/gi, "")
		.replace(/&nbsp;/g, " ")
		.trim();
}
